import {
  createStyles,
  Group,
  Title,
  Text,
  Card,
  Container,
  Box,
} from "@mantine/core";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useEffect, useState, lazy } from "react";
import axios from "../../api/axios";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { BsPencil } from "react-icons/bs";
import ImageContent from "../dynamic/ImageContent";

const FetchImage = lazy(() => import("../dynamic/fetchImage"));

const useStyles = createStyles((theme) => ({
  title: {
    [theme.fn.smallerThan("sm")]: {
      fontSize: "24px",
    },
  },

  description: {
    maxWidth: 600,
    margin: "auto",
    fontWeight: 200,
  },

  card: {
    transition: "transform 150ms ease, box-shadow 150ms ease",

    "&:hover": {
      transform: "scale(1.01)",
      boxShadow: theme.shadows.md,
    },
  },

  addbtn: {
    backgroundColor: "green",
    padding: "10px 20px",
    textDecoration: "none",
    fontWeight: 600,
  },
}));

export default function Blog() {
  const { auth, username } = useAuth();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const languagePrefix = window.location.pathname.startsWith("/nl")
    ? "/nl"
    : window.location.pathname.startsWith("/de")
    ? "/de"
    : "";

  const [blogs, setBlogs] = useState([]);
  const [featureblog, setFeatureBlog] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getBlogs = async () => {
      try {
        const res = await axios.get("/blog-web", {
          params: { username },
        });

        // Filter blogs where user_id === author_id
        const userBlogs = res.data;

        setBlogs(userBlogs);
        setFeatureBlog(userBlogs[0]);
        setLoading(false);
      } catch (error) {
        console.log("Error fetching data:", error);
        setLoading(false);
      }
    };

    getBlogs();
  }, [username, auth?.user_id]);

  // console.log(auth.role);

  const handleVisibilityToggle = async (id, newVisibility) => {
    const confirmMessage = newVisibility
      ? "Do you want to make this blog visible to users?"
      : "Do you want to make this blog invisible to users?";

    if (window.confirm(confirmMessage)) {
      try {
        await axios.put(`/blog/${id}/visibility`, {
          is_visible: newVisibility,
        });
        setBlogs((prevBlogs) =>
          prevBlogs.map((blog) =>
            blog.id === id ? { ...blog, is_visible: newVisibility } : blog
          )
        );
      } catch (error) {
        console.error("Error updating blog visibility:", error);
      }
    }
  };

  const filtered_blogs = blogs
    .filter((blog) =>
      languagePrefix === "/nl"
        ? blog.language === "NL"
        : languagePrefix === "/de"
        ? blog.language === "DE"
        : blog.language === "EN"
    )
    .filter((blog) => auth?.role === "User" || blog.is_visible);

  const { t } = useTranslation();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  return (
    <Box>
        {!auth?.role ? (
                <FetchImage title="blogs-main" type="image-about" />
              ) : (
                <ImageContent
                  title="blogs-main"
                  username={username}
                  type="image-about"
                />
              )}
      {/* <Image src={blogPost} alt="Landscape" height={400}></Image> */}

      {auth.role ? (
        <Container size="lg" py={100}>
          <Group position="center">
            <h2>{t("Blogs")}</h2>
          </Group>
          <Group position="center">
            {auth.role && (
              <Link
                className={classes.addbtn}
                to="/edit/blog/add"
                onClick={(e) => {
                  if (!auth.role) {
                    e.preventDefault(); // stop navigation
                    return;
                  }

                  // Delay and force reload to ensure route loads cleanly
                  setTimeout(() => {
                    window.location.href = "/edit/blog/add";
                  }, 100); // give a moment to trigger navigation
                }}
              >
                Add New Blog
              </Link>
            )}
          </Group>
          <Title order={2} className={classes.title} ta="center" mt="sm">
            {languagePrefix === "/nl"
              ? "Al onze laatste artikelen"
              : languagePrefix === "/de"
              ? "Unsere neuesten Blogbeiträge"
              : "All the latest articles"}
          </Title>

          <Container align="center" pt="xl">
            {loading ? (
              <Text>Loading blogs...</Text>
            ) : filtered_blogs.length === 0 ? (
              <Text>No blogs available for this user.</Text> // Show message if no blogs
            ) : (
              [...filtered_blogs].reverse().map((post) => (
                <Card
                  key={post.id}
                  mt="xl"
                  m="md"
                  p="md"
                  radius="md"
                  onClick={() => {
                    auth.role
                      ? navigate(`/edit/blog/${post?.id}`)
                      : navigate(`/blog/${post?.id}`);
                    window.scrollTo(0, 0);
                  }}
                  className={classes.card}
                  shadow="xl"
                  withBorder
                  style={{ position: "relative" }}
                >
                  {/* Visibility toggle button */}
                  {/* Edit button */}

                  <div
                    style={{
                      position: "absolute",
                      top: "10px",
                      left: "10px",
                      gap: "10px",
                      // background: "#ef7e2e",

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    className=""
                  >
                    {auth.role && (
                      <Link
                        to={`/edit/blog/${post?.id}`}
                        style={{
                          color: "#000000 !important",
                          border: "none",
                          borderRadius: "50%",
                          width: "30px",
                          height: "30px",

                          cursor: "pointer",
                          fontSize: "16px",
                          textDecoration: "none",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <BsPencil size="25px" />
                      </Link>
                    )}
                    {auth.role &&
                      (post?.is_visible ? (
                        <IconEye
                          style={{
                            cursor: "pointer",
                            color: "green",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisibilityToggle(post.id, false);
                          }}
                        />
                      ) : (
                        <IconEyeOff
                          style={{
                            cursor: "pointer",
                            color: "red",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisibilityToggle(post.id, true);
                          }}
                        />
                      ))}
                  </div>

                  <Title
                    order={4}
                    weight={200}
                    align="center"
                    color="resirent"
                    mb="md"
                  >
                    {post?.date && formatDate(post?.date)}
                  </Title>
                  <Title order={3} weight={100} align="center">
                    {post.title}
                  </Title>
                  <Text
                    c="dimmed"
                    className={classes.description}
                    ta="center"
                    mt="md"
                  >
                    {post.description}
                  </Text>
                </Card>
              ))
            )}
          </Container>
        </Container>
      ) : (
        <div className="container">
          <h1 className="padding-top">Blogs</h1>
            {blogs.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: "2rem", fontStyle: "italic" }}>
              No blogs are available at the moment. Please check back soon!
            </div>
          ) : (
          <div className="feature-blog-container"
             onClick={() => {
              if (featureblog?.id) {
                navigate(`/blog/${featureblog.id}`);
                window.scrollTo(0, 0);
              }
            }}
            style={{ cursor: "pointer" }}>
            <div className="feature-blog-img">
              {featureblog && <img src={featureblog.imageUrl} alt="" />}
            </div>
            <div className="feature-blog-content">
              <div className="feature-blog-title">{featureblog?.title}</div>
              <div className="feature-blog-description">
                {featureblog?.description}
              </div>
              <div className="feature-blog-date">
                {featureblog?.date && formatDate(featureblog?.date)}
              </div>
            </div>
          </div>
          )}

          <h2 className="related-blogs padding-top">
            {" "}
            {languagePrefix === "/nl"
              ? "Al onze laatste artikelen"
              : languagePrefix === "/de"
              ? "Unsere neuesten Blogbeiträge"
              : "All the latest articles"}
          </h2>
          {blogs.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: "2rem", fontStyle: "italic" }}>
              No blogs are available at the moment. Please check back soon!
            </div>
          ) : (
          <div className="blog-grid">
            {blogs.slice(0, 12).map((item, itemIndex) => {
              return (
                <div className="blog-gird-item" onClick={() => {
                  navigate(`/blog/${item.id}`);
                  window.scrollTo(0, 0);
                }}
                style={{ cursor: "pointer" }}>
                  <img src={item?.imageUrl} alt="" />
                  <div className="blog-gird-item-date">
                    {item?.date && formatDate(item?.date)}
                  </div>
                  <div className="blog-gird-item-title">
                    {item?.title && item?.title}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}
    </Box>
  );
}
